<!--
  BaseFilterDialog.vue — Filter dialog wrapper extending BaseDialog with filter defaults.

  Filtering is explicit: changes only apply when the user clicks "Apply".
  - Apply: emits @save, then closes
  - Clear: emits @clear (parent resets filters), then closes
  - X button / click-outside: same as Clear (discard changes, reset filters)

  Filter dialogs should use local copies of their filter state and only commit
  to the real filter refs in their @save handler.

  Props: Same as BaseDialog with overridden defaults.
  Events:
    save  — emitted when Apply button is clicked (commit local → real filters)
    clear — emitted when Clear/X is clicked (reset filters to defaults)
-->
<template>
  <BaseDialog
    :model-value="modelValue"
    @update:model-value="handleModelUpdate"
    :title="title"
    :subtitle="subtitle"
    :title-icon="titleIcon"
    :title-icon-color="titleIconColor"
    :max-width="maxWidth"
    :persistent="persistent"
    :scrollable="scrollable"
    :save-text="saveText"
    :save-color="saveColor"
    :save-loading="saveLoading"
    :save-disabled="saveDisabled"
    :show-save="showSave"
    :cancel-text="cancelText"
    cancel-action="emit"
    :toolbar-color="toolbarColor"
    @cancel="onClear"
    @save="onDone"
  >
    <slot />
    <template #actions><slot name="actions" /></template>
    <template #toolbar-extra><slot name="toolbar-extra" /></template>
  </BaseDialog>
</template>

<script setup>
import BaseDialog from "@/components/BaseDialog.vue";

defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: "Filters" },
  subtitle: { type: String, default: null },
  maxWidth: { type: [Number, String], default: 500 },
  persistent: { type: Boolean, default: false },
  scrollable: { type: Boolean, default: true },
  saveText: { type: String, default: "Apply" },
  saveColor: { type: String, default: "primary" },
  saveLoading: { type: Boolean, default: false },
  saveDisabled: { type: Boolean, default: false },
  showSave: { type: Boolean, default: true },
  cancelText: { type: String, default: "Clear" },
  toolbarColor: { type: String, default: "primary" },
  titleIcon: { type: String, default: null },
  titleIconColor: { type: String, default: undefined },
});

const emit = defineEmits(["update:modelValue", "save", "clear"]);

// Flag to distinguish action-button closes from X/click-outside closes
let fromAction = false;

/** Clear button: emit clear event then close dialog */
function onClear() {
  fromAction = true;
  emit("clear");
  emit("update:modelValue", false);
}

/** Apply button: emit save event then close dialog */
function onDone() {
  fromAction = true;
  emit("save");
  emit("update:modelValue", false);
}

/**
 * Intercept model-value changes from BaseDialog.
 * When the dialog closes via X button or click-outside (not from our
 * action buttons), treat it the same as Clear — reset filters.
 */
function handleModelUpdate(val) {
  if (val) {
    // Dialog opening — reset flag and forward
    fromAction = false;
    emit("update:modelValue", val);
    return;
  }
  // Dialog closing
  if (!fromAction) {
    // Closed via X button or click-outside — treat as Clear
    emit("clear");
  }
  emit("update:modelValue", val);
  fromAction = false;
}
</script>
